<template>
  <v-card>
    <v-card-title>
      Send notification
    </v-card-title>

    <v-divider />

    <v-form
      ref="form"
      v-model="isFormValid"
      @submit.prevent="onSubmit"
    >
      <v-card-text class="overflow-auto px-0 pt-0 pb-3" style="height: calc(100vh - 250px)">
        <v-container>
          <v-row class="mx-0 mb-1 d-flex">

            <div class="templates d-flex" style="gap: 1rem">
              <v-select
                v-if="hasNotificationTemplateAccess"
                v-model="selectedTemplate"
                :items="notificationTemplates"
                :loading="isLoading"
                item-text="name"
                label="Template"
                return-object
                hide-details
                @input="onApplyTemplate"
              />

              <v-select
                v-if="selectedTemplate?.languages?.length > 1"
                label="Language"
                :items="selectedTemplate.languages"
                :value="language"
                hide-details
                style="max-width: 150px"
                @change="onApplyTemplate(selectedTemplate, $event)"
              />
            </div>

            <div class="checkboxes d-flex ml-5" style="gap: 1rem">
              <v-checkbox
                v-model="isBaseEnabled"
                hide-details
                label="List"
              />
              <v-checkbox
                v-model="isToastEnabled"
                hide-details
                label="Toast"
              />
              <v-checkbox
                v-model="isPopupEnabled"
                hide-details
                label="Pop-up"
              />
            </div>
          </v-row>
        </v-container>

        <fieldset class="rounded-8 mx-4" v-if="isBaseEnabled">
          <legend class="ml-4 px-2">List</legend>
          <v-text-field
            v-model="notification.base.header"
            :disabled="!isBaseEnabled"
            class="ma-4"
            :error="isBaseEnabled && !notification.base.header"
            :rules="isBaseEnabled ? [v => !!v || 'Header is required', () => isBaseEnabled] : []"
            label="Header"
          />
          <v-textarea
            v-model="notification.base.content"
            :disabled="!isBaseEnabled"
            auto-grow
            :error="isBaseEnabled && !notification.base.content"
            :rules="isBaseEnabled ? [v => !!v || 'Content is required', () => isBaseEnabled] : []"
            class="mx-4 mt-2 pt-0"
            label="Content"
            rows="3"
          />
          <v-textarea
            v-model="notification.base.excerpt"
            :disabled="!isBaseEnabled"
            auto-grow
            :error="isBaseEnabled && !notification.base.excerpt"
            :rules="isBaseEnabled ? [v => !!v || 'Excerpt is required', () => isBaseEnabled] : []"
            class="mx-4 mt-2 pt-0"
            label="Excerpt"
            rows="3"
          />
        </fieldset>


        <fieldset class="rounded-8 mx-4 mt-4" v-if="isToastEnabled">
          <legend class="ml-4 px-2">Toast</legend>
          <v-textarea
            v-model="notification.toast.excerpt"
            :disabled="!isToastEnabled"
            auto-grow
            :error="isToastEnabled && !notification.toast.excerpt"
            :rules="isToastEnabled ? [v => !!v || 'Toast excerpt is required', () => isToastEnabled] : []"
            class="mx-4 mt-2"
            label="Excerpt"
            rows="3"
          />
        </fieldset>

        <fieldset class="rounded-8 mx-4 mt-4" v-if="isPopupEnabled">
          <legend class="ml-4 px-2">Popup</legend>
          <v-text-field
            v-model="notification.popup.header"
            :disabled="!isPopupEnabled"
            auto-grow
            :error="isPopupEnabled && !notification.popup.header"
            :rules="isPopupEnabled ? [v => !!v || 'Popup header is required', () => isPopupEnabled] : []"
            class="ma-4"
            label="Header"
          />
          <v-textarea
            v-model="notification.popup.content"
            :disabled="!isPopupEnabled"
            auto-grow
            :error="isPopupEnabled && !notification.popup.content"
            :rules="isPopupEnabled ? [v => !!v || 'Popup content is required', () => isPopupEnabled] : []"
            class="mx-4 mt-2 pt-0"
            label="Content"
            rows="3"
          />
          <v-textarea
            v-model="notification.popup.excerpt"
            :disabled="!isPopupEnabled"
            auto-grow
            :error="isPopupEnabled && !notification.popup.excerpt"
            :rules="isPopupEnabled ? [v => !!v || 'Popup excerpt is required', () => isPopupEnabled] : []"
            class="mx-4 mt-2 pt-0"
            label="Excerpt"
            rows="3"
          />
        </fieldset>

        <fieldset class="rounded-8 mx-4 mt-4">
          <legend class="ml-4 px-2">URL</legend>
          <v-row class="mx-0 pl-4 mt-1 mb-3 mr-4">
            <v-text-field
              v-model="notification.url.header"
              hide-details="auto"
              class="col-4"
              label="Title"
            ></v-text-field>
            <v-text-field
              v-model="notification.url.content"
              hide-details="auto"
              class="col-8"
              label="Link"
            ></v-text-field>
          </v-row>
        </fieldset>


      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-row class="ml-0 mr-2" style="gap: 1rem">
          <v-spacer />
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            @click="$emit('cancel')"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="isLoading"
            :disabled="!isFormValid || isLoading"
            type="submit"
          >
            Send
          </v-btn>
        </v-row>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>

  const getDefaultNotification = () => ({
    base: {
      header: '',
      content: '',
      excerpt: '',
    },
    toast: {
      excerpt: '',
    },
    popup: {
      header: '',
      content: '',
      excerpt: '',
    },
    url: {
      header: '',
      content: '',
    }
  })

  export default {
    props: {
      language: String,
      isLoading: Boolean,
      notificationTemplates: {
        type: Array,
        default: () => ([])
      },
      hasNotificationTemplateAccess: Boolean
    },
    data () {
      return {
        notification: getDefaultNotification(),
        isBaseEnabled: false,
        isToastEnabled: false,
        isPopupEnabled: false,
        isFormValid: false,
        selectedTemplate: null
      }
    },

    methods: {
      onSubmit () {
        const notification = {
          url: { ...this.notification.url }
        }

        if (this.isBaseEnabled) {
          notification.base = { ...this.notification.base }
        }

        if (this.isToastEnabled) {
          notification.toast = { ...this.notification.toast }
        }

        if (this.isPopupEnabled) {
          notification.popup = { ...this.notification.popup }
        }

        this.$emit('send-notification', notification)
      },

      onApplyTemplate ({ template }, language = this.language) {
        Object.assign(this.notification, getDefaultNotification())
        this.isBaseEnabled = this.isToastEnabled = this.isPopupEnabled = false

        const baseTemplate = template.base[language] || template.base['EN'] || template.base[Object.keys(template.base)[0]]
        const toastTemplate = template.toast[language] || template.toast['EN'] || template.base[Object.keys(template.toast)[0]]
        const popupTemplate = template.popup[language] || template.popup['EN'] || template.base[Object.keys(template.popup)[0]]
        const urlTemplate = template.url[language] || template.url['EN'] || template.base[Object.keys(template.url)[0]]

        if (baseTemplate) {
          Object.assign(this.notification.base, baseTemplate)
          this.isBaseEnabled = true
        }

        if (toastTemplate) {
          Object.assign(this.notification.toast, toastTemplate)
          this.isToastEnabled = true
        }

        if (popupTemplate) {
          Object.assign(this.notification.popup, popupTemplate)
          this.isPopupEnabled = true
        }

        if (urlTemplate) {
          Object.assign(this.notification.url, urlTemplate)
        }
      }
    }
  }
</script>
